// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Templates owned by this application, linking to the template studio (AREA-TEMPLATES).
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileText, Plus, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { TemplateSummary } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { appTemplatesQuery } from '@/features/ops/api'
import { CreateTemplateDialog } from '@/features/ops/CreateTemplateDialog'

export const Route = createFileRoute('/applications/$appId/templates')({
  staticData: { crumb: 'Templates' },
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
  }),
  component: AppTemplatesPage,
})

const col = tableColumns<TemplateSummary>()
const columns = col.columns([
  col.accessor('key', {
    header: 'Template',
    cell: (c) => (
      <div className="min-w-0">
        <div className="truncate font-mono text-[13px] text-ink">{c.getValue()}</div>
        <div className="truncate text-xs text-ink-3">{c.row.original.name}</div>
      </div>
    ),
    enableHiding: false,
  }),
  col.accessor('format', {
    header: 'Format',
    cell: (c) => (c.getValue() === 'mjml' ? 'MJML' : 'HTML'),
    meta: { width: '6rem' },
  }),
  col.accessor((t) => t.active_version ?? -1, {
    id: 'active_version',
    header: 'Live version',
    cell: (c) =>
      c.row.original.active_version != null ? (
        <span className="tabular">v{c.row.original.active_version}</span>
      ) : (
        <span className="text-warning">Not published</span>
      ),
    meta: { width: '9rem', label: 'Live version' },
  }),
  col.accessor('has_draft', {
    header: 'Draft',
    cell: (c) =>
      c.getValue() ? (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="brass">Unpublished changes</Badge>
          {c.row.original.draft_updated_at && (
            <RelativeTime value={c.row.original.draft_updated_at} className="text-xs text-ink-3" />
          )}
        </span>
      ) : (
        <span className="text-ink-3">—</span>
      ),
  }),
  col.accessor('updated_at', {
    header: 'Updated',
    cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
    sortFn: 'datetime',
    meta: { align: 'right', width: '8rem' },
  }),
])

function AppTemplatesPage() {
  const { appId } = Route.useParams()
  const { q = '' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { can } = usePermissions()
  const [creating, setCreating] = useState(false)
  const list = useQuery(appTemplatesQuery(appId))

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const items = list.data ?? []
    if (!needle) return items
    return items.filter((t) => t.key.includes(needle) || t.name.toLowerCase().includes(needle))
  }, [list.data, q])

  const setQ = (v: string) =>
    void navigate({ search: (prev) => ({ ...prev, q: v || undefined }), replace: true })

  return (
    <>
      <DataTable
        aria-label="Templates"
        columns={columns}
        data={filtered}
        getRowId={(t) => t.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        initialSorting={[{ id: 'key', desc: false }]}
        persistKey="app-templates"
        onRowClick={(t) =>
          void navigate({ to: '/templates/$templateId', params: { templateId: t.id } })
        }
        toolbar={
          <>
            <Input
              className="w-64"
              size="sm"
              placeholder="Filter by key or name"
              aria-label="Filter templates"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              leading={<Search />}
              trailing={
                q ? (
                  <IconButton
                    label="Clear filter"
                    size="xs"
                    tooltip={false}
                    onClick={() => setQ('')}
                  >
                    <X />
                  </IconButton>
                ) : undefined
              }
            />
            <div className="ml-auto">
              <Can permission="edit_content" mode="disable">
                <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                  New template
                </Button>
              </Can>
            </div>
          </>
        }
        empty={
          q ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching templates"
              description={`Nothing matches “${q}”.`}
            />
          ) : (
            <EmptyState
              icon={FileText}
              title="No templates yet"
              description="Templates hold the subject and body of each email. Shared library templates are listed under Templates in the sidebar."
              action={
                can('edit_content') ? (
                  <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                    New template
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
      <CreateTemplateDialog appId={appId} open={creating} onOpenChange={setCreating} />
    </>
  )
}
